import AddIcon from '@mui/icons-material/Add';
import { Box, Checkbox, Grid, IconButton, Switch } from '@mui/material';
import { FaRegEdit } from 'react-icons/fa';
import { CustomFieldEnum, ICustomField } from 'wisestep-api';
import { WsButton, WsColors, WsDataTable } from 'wisestep-base';

import { FieldIconMap } from '../../features/invoicing/helper/FieldHelper';
import { success, failed } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import { IOption } from '../SettingsIndex';
import { SettingsPageLayout } from '../SettingsPageLayout';
import { IBreadCrumbsList } from '../WsBreadCrumbs';

export interface WsCustomFieldProps {
  title: string;
  entityType: CustomFieldEnum;
  breadCrumbList: IBreadCrumbsList[];
  indexOptions: IOption[];
  allFields: ICustomField[];
  fieldsAlwaysMandatory?: string[];
  updateService: (field: ICustomField) => void;
  createService: (field: ICustomField) => void;
  onEditField?: (field?: ICustomField) => void;
}

export const WsCustomField = ({
  title,
  breadCrumbList,
  indexOptions,
  allFields,
  fieldsAlwaysMandatory,
  updateService,
  createService,
  entityType,
  onEditField,
}: WsCustomFieldProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);

  const handleVisibilityChange = async (checked: boolean, row) => {
    try {
      await updateService({ ...row, visible: checked });
      setNotification(success('Custom Field saved successfully!'));
    } catch (error) {
      console.error('Error updating visibility:', error);
      setNotification(failed('Failed to update Custom Field visibility.'));
    }
  };

  const handleRequiredChange = async (event: React.ChangeEvent<HTMLInputElement>, row) => {
    try {
      const checked = event.target.checked;
      if (row.isDefault && !row.id) {
        await createService({
          ...row,
          required: checked,
          name: row.name,
          entity: entityType,
          isDefault: true,
          visible: true,
        });
      } else {
        await updateService({ ...row, required: checked });
      }
      setNotification(success('Custom Field saved successfully!'));
    } catch (error) {
      console.error('Error updating required status:', error);
      setNotification(failed('Failed to update Custom Field.'));
    }
  };

  const handleAdvanceFilterChange = async (checked: boolean, row) => {
    try {
      await updateService({ ...row, isAdvancedSearchEnabled: checked });
      setNotification(success('Custom Field saved successfully!'));
    } catch (error) {
      console.error('Error updating advanced Search:', error);
      setNotification(failed('Failed to update Custom Field advanced Search Enabled.'));
    }
  };
  const columns = [
    {
      Header: 'Attribute',
      accessor: 'Attribute',
      Cell: ({ row: { original: row } }: { row: { original: ICustomField } }) => {
        const Icon = FieldIconMap[row.type ?? ''];
        return (
          <Grid container direction="row" alignItems="center">
            <Icon />
            <Grid item style={{ marginLeft: '1rem' }}>
              {row.label}
            </Grid>
          </Grid>
        );
      },
    },
    {
      Header: 'Visible',
      accessor: 'Visible',
      Cell: ({ row: { original: row } }: { row: { original: ICustomField } }) =>
        row.isDefault != true && (
          <Switch
            checked={row.visible}
            onChange={(_event, checked) => handleVisibilityChange(checked, row)}
          />
        ),
    },
    {
      Header: 'Mandatory',
      accessor: 'Mandatory',
      Cell: ({ row: { original: row } }: { row: { original: ICustomField } }) => (
        <Checkbox
          checked={fieldsAlwaysMandatory?.includes(row.name ?? '') ? true : row.required}
          disabled={fieldsAlwaysMandatory?.includes(row.name ?? '')}
          onChange={(event) => handleRequiredChange(event, row)}
        />
      ),
    },
    {
      Header: 'Advanced Search',
      accessor: 'isAdvancedSearchEnabled',
      Cell: ({ row: { original: row } }: { row: { original: ICustomField } }) =>
        !row.isDefault && (
          <Switch
            checked={row.isAdvancedSearchEnabled}
            onChange={(_event, checked) => handleAdvanceFilterChange(checked, row)}
          />
        ),
    },
    {
      Header: '',
      accessor: 'accessor',
      Cell: ({ row: { original: row } }: { row: { original: ICustomField } }) =>
        !row.isDefault && (
          <IconButton sx={{ color: WsColors.TheBlues500 }} onClick={() => onEditField?.(row)}>
            <FaRegEdit />
          </IconButton>
        ),
    },
  ];

  return (
    <SettingsPageLayout title={title} indexOptions={indexOptions} breadCrumnbsList={breadCrumbList}>
      {allFields && (
        <Box display="flex" flexDirection="column" alignItems="flex-end" px={2}>
          <Box my={1}>
            <WsButton onClick={() => onEditField?.()} startIcon={<AddIcon />}>
              Add Field
            </WsButton>
          </Box>
          <WsDataTable elevated={false} dense columns={columns} data={[...allFields] as []} />
        </Box>
      )}
    </SettingsPageLayout>
  );
};
